import { useState, useEffect } from 'react'
import {
  ArrowLeftIcon,
  EditIcon,
  SaveIcon,
  XIcon,
  UserIcon,
  FileTextIcon,
  LinkIcon,
  UnlinkIcon,
  PlusIcon,
  AlertCircleIcon,
  CheckCircleIcon,
} from 'lucide-react'
import { Student, Document, UpdateStudentRequest } from '../types'
import { formatDate, formatFileSize, getStudentFullName } from '../utils/helpers'
import { updateStudent, linkDocumentToStudent, unlinkDocumentFromStudent, getDocuments } from '../utils/api'

interface StudentDetailProps {
  student: Student
  onBack: () => void
  onStudentUpdated: () => void
  onSelectDocument?: (document: Document) => void
}

export const StudentDetail = ({
  student,
  onBack,
  onStudentUpdated,
  onSelectDocument,
}: StudentDetailProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState<UpdateStudentRequest>({
    first_name: student.first_name,
    last_name: student.last_name,
    date_of_birth: student.date_of_birth || '',
    grade_level: student.grade_level || '',
    school_name: student.school_name || '',
    student_id_number: student.student_id_number || '',
    notes: student.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [availableDocuments, setAvailableDocuments] = useState<Document[]>([])
  const [showLinkDocuments, setShowLinkDocuments] = useState(false)
  const [linking, setLinking] = useState<Set<string>>(new Set())
  const [unlinking, setUnlinking] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadAvailableDocuments()
  }, [])

  const loadAvailableDocuments = async () => {
    try {
      const docs = await getDocuments()
      setAvailableDocuments(docs)
    } catch (error) {
      console.error('Failed to load documents:', error)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      
      // Filter out empty strings to send undefined for optional fields
      const cleanForm = Object.entries(editForm).reduce((acc, [key, value]) => {
        if (value !== '') {
          acc[key as keyof UpdateStudentRequest] = value as any
        }
        return acc
      }, {} as UpdateStudentRequest)

      await updateStudent(student.id, cleanForm)
      setIsEditing(false)
      onStudentUpdated()
    } catch (error: any) {
      setError(error.message || 'Failed to update student')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditForm({
      first_name: student.first_name,
      last_name: student.last_name,
      date_of_birth: student.date_of_birth || '',
      grade_level: student.grade_level || '',
      school_name: student.school_name || '',
      student_id_number: student.student_id_number || '',
      notes: student.notes || '',
    })
    setIsEditing(false)
    setError(null)
  }

  const handleLinkDocument = async (document: Document) => {
    try {
      setLinking(prev => new Set(prev).add(document.id))
      await linkDocumentToStudent(student.id, document.id)
      onStudentUpdated()
      loadAvailableDocuments()
    } catch (error: any) {
      alert(`Error linking document: ${error.message}`)
    } finally {
      setLinking(prev => {
        const updated = new Set(prev)
        updated.delete(document.id)
        return updated
      })
    }
  }

  const handleUnlinkDocument = async (document: Document) => {
    if (!window.confirm(`Unlink "${document.original_filename}" from ${getStudentFullName(student)}?`)) {
      return
    }

    try {
      setUnlinking(prev => new Set(prev).add(document.id))
      await unlinkDocumentFromStudent(student.id, document.id)
      onStudentUpdated()
      loadAvailableDocuments()
    } catch (error: any) {
      alert(`Error unlinking document: ${error.message}`)
    } finally {
      setUnlinking(prev => {
        const updated = new Set(prev)
        updated.delete(document.id)
        return updated
      })
    }
  }

  const linkedDocumentIds = new Set(student.documents?.map(doc => doc.id) || [])
  const unlinkedDocuments = availableDocuments.filter(doc => !linkedDocumentIds.has(doc.id))

  const getStatusIcon = () => {
    if (student.is_active === 0) {
      return <AlertCircleIcon className="h-5 w-5 text-red-500" />
    }
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />
  }

  const getStatusText = () => {
    if (student.is_active === 0) {
      return 'Inactive'
    }
    return 'Active'
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Students
        </button>
        
        <div className="flex items-center space-x-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              <EditIcon className="h-4 w-4 mr-1" />
              Edit
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancel}
                className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                <XIcon className="h-4 w-4 mr-1" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" />
                ) : (
                  <SaveIcon className="h-4 w-4 mr-1" />
                )}
                Save
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Student Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-center mb-6">
          <UserIcon className="h-8 w-8 text-blue-500 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getStudentFullName(student)}
            </h1>
            <div className="flex items-center mt-1">
              {getStatusIcon()}
              <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                student.is_active === 0 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {getStatusText()}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="First name"
                    required
                  />
                ) : (
                  <p className="text-gray-900">{student.first_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Last name"
                    required
                  />
                ) : (
                  <p className="text-gray-900">{student.last_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={editForm.date_of_birth}
                    onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                ) : (
                  <p className="text-gray-900">
                    {student.date_of_birth ? formatDate(student.date_of_birth) : 'Not specified'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* School Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">School Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.grade_level}
                    onChange={(e) => setEditForm({ ...editForm, grade_level: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 5th, K, 12"
                  />
                ) : (
                  <p className="text-gray-900">{student.grade_level || 'Not specified'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.school_name}
                    onChange={(e) => setEditForm({ ...editForm, school_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="School name"
                  />
                ) : (
                  <p className="text-gray-900">{student.school_name || 'Not specified'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student ID</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.student_id_number}
                    onChange={(e) => setEditForm({ ...editForm, student_id_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Student ID number"
                  />
                ) : (
                  <p className="text-gray-900">{student.student_id_number || 'Not specified'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          {isEditing ? (
            <textarea
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about the student..."
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 p-3 rounded-md">
              {student.notes || 'No notes added yet.'}
            </p>
          )}
        </div>

        {/* Metadata */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center text-sm text-gray-500 space-x-4">
            <span>Added {formatDate(student.created_at)}</span>
            {student.updated_at && student.updated_at !== student.created_at && (
              <span>Updated {formatDate(student.updated_at)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Associated Documents</h2>
          <button
            onClick={() => setShowLinkDocuments(!showLinkDocuments)}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <LinkIcon className="h-4 w-4 mr-1" />
            Link Documents
          </button>
        </div>

        {/* Link Documents Section */}
        {showLinkDocuments && unlinkedDocuments.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-sm font-medium text-blue-900 mb-3">Available Documents to Link</h3>
            <div className="space-y-2">
              {unlinkedDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between bg-white p-3 rounded border">
                  <div className="flex items-center">
                    <FileTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.original_filename}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(doc.file_size)} • {formatDate(doc.upload_date)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleLinkDocument(doc)}
                    disabled={linking.has(doc.id)}
                    className="flex items-center px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {linking.has(doc.id) ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
                    ) : (
                      <PlusIcon className="h-3 w-3 mr-1" />
                    )}
                    Link
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Linked Documents */}
        {student.documents && student.documents.length > 0 ? (
          <div className="space-y-3">
            {student.documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-4 rounded-md">
                <div 
                  className="flex items-center flex-1 cursor-pointer"
                  onClick={() => onSelectDocument && onSelectDocument(doc)}
                >
                  <FileTextIcon className="h-5 w-5 text-blue-500 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">{doc.original_filename}</h4>
                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>Uploaded {formatDate(doc.upload_date)}</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        doc.processing_status === 'completed' ? 'bg-green-100 text-green-700' :
                        doc.processing_status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                        doc.processing_status === 'error' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {doc.processing_status}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleUnlinkDocument(doc)}
                  disabled={unlinking.has(doc.id)}
                  className="ml-4 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                  title="Unlink document"
                >
                  {unlinking.has(doc.id) ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
                  ) : (
                    <UnlinkIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FileTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents linked</h3>
            <p className="text-gray-500">
              Link documents to this student to organize their educational materials.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}